import React from 'react';
import {
  Alert,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CardActions from 'src/pages/sistema/components/CardActions';
import { FieldView } from 'src/pages/sistema/components/FieldView';
import { OperacionLCLS } from '../types/types';
import { useLCLSCuentaLimites } from '../hooks/useLCLSCuentaLimites';
import { useLCLSLimiteTipos } from '../hooks/useLCLSCache';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import { EntityWithIdFields, QueryStatus } from 'src/types';
import { LimitCellLayout, OperacionLCLSLimiteFila } from "./OperacionLCLSLimiteFila";
import { OperacionLCLSCuentaFields } from "../types/fields";

interface SeccionLCLSLimitesTablaProps {
  operacion: OperacionLCLS;
}

const SeccionLCLSLimitesTabla = ({ operacion }: SeccionLCLSLimitesTablaProps) => {
  const { cuentas, status, refetch } = useLCLSCuentaLimites(operacion[EntityWithIdFields.Id]);

  const limitesTipos = useLCLSLimiteTipos();
  const loadLimitesOptions = useLoadOptions({
    data: limitesTipos.limiteTipos,
    loading: limitesTipos.isLoading,
    isError: limitesTipos.isError,
    error: limitesTipos.error,
  });

  if (status === QueryStatus.Loading) {
    return <Typography>Cargando límites...</Typography>;
  }

  if (status === QueryStatus.Error || !cuentas) {
    return <Alert severity="error">No se pudieron cargar los límites de la operación.</Alert>;
  }

  return (
    <CardActions title='Límites de la Operación' icon='mdi:currency-usd' colapsable>
      {cuentas.map((cuenta) => (
        <Card key={cuenta.id} variant="outlined" sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={2.4}>
                <FieldView label='Número de Cuenta' value={cuenta[OperacionLCLSCuentaFields.NroCuenta]} />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <FieldView label='Razón Social' value={cuenta[OperacionLCLSCuentaFields.RazonSocial]} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FieldView label='Tipo Cliente' value={cuenta[OperacionLCLSCuentaFields.DescClienteTipo]} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FieldView label='Sub-tipo de Cliente' value={cuenta[OperacionLCLSCuentaFields.DescClienteSubTipo]} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FieldView label='Moneda' value={cuenta[OperacionLCLSCuentaFields.Moneda]} />
              </Grid>
            </Grid>
          </CardContent>
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>
                  <TableCell variant="head">
                    <Typography variant="body2">Participante</Typography>
                  </TableCell>
                  <TableCell variant="head" align="right">
                    <LimitCellLayout>
                      <Typography variant="body2">Límite de concertación</Typography>
                    </LimitCellLayout>
                  </TableCell>
                  <TableCell variant="head" align="right">
                    <LimitCellLayout>
                      <Typography variant="body2">Límite de liquidación DVP</Typography>
                    </LimitCellLayout>
                  </TableCell>
                  <TableCell variant="head" align="right">
                    <LimitCellLayout>
                      <Typography variant="body2">Límite de liquidación FOP</Typography>
                    </LimitCellLayout>
                  </TableCell>

                  <TableCell variant="head">
                    <Typography variant="body2">Fecha de la acción</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cuenta.limites.map((limite) => (
                  <OperacionLCLSLimiteFila
                    key={limite.id}
                    operacion={operacion}
                    limite={limite}
                    loadLimitesOptions={loadLimitesOptions}
                    refetch={refetch}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ))}
    </CardActions>
  );
}

export default SeccionLCLSLimitesTabla;
